/**
 * Uebersetzt die Rohdaten aus MOCO und open-banking.io in das Domaenenmodell.
 *
 * Bewusst hier und nicht in den Cloud Functions: das ist reine Abbildungslogik
 * ohne I/O, und genau hier entstehen die Fehler, die spaeter niemand mehr
 * findet — eine falsch gemappte Statusliste laesst Rechnungen lautlos aus der
 * Prognose verschwinden. Als reine Funktionen sind sie testbar.
 *
 * Zwei Entscheidungen, die sich durch die ganze Datei ziehen:
 *
 *  - Die Umsatzsteuer wird *nicht* aus einem Feld uebernommen, sondern als
 *    `brutto - netto` berechnet. MOCOs `tax`-Feld ist je nach Endpunkt mal ein
 *    Satz und mal ein Betrag; die Differenz stimmt immer.
 *  - Was in MOCO bereits als Zahlung erfasst ist, wird als `matchedCents`
 *    uebernommen. Damit erkennt das Tool Zahlungen, die ihr weiterhin in MOCO
 *    bucht, und zaehlt sie nicht doppelt.
 */
import { dayInMonth, monthRange, nthLastBusinessDayOfMonth } from "./dates.js";
import { signedCents, toCents } from "./money.js";
import { normalizeIban } from "./normalize.js";
// ---- Statusabbildung ---------------------------------------------------------
/**
 * MOCO-Rechnungsstatus, die keinen erwarteten Zahlungseingang darstellen.
 *
 * `draft` ist noch nicht gestellt und damit keine Forderung — eine Prognose
 * darauf waere Wunschdenken. `ignored` wurde bewusst ausgebucht.
 */
const INVOICE_STATUS_IGNORED = new Set(["draft", "ignored"]);
const INVOICE_STATUS_SETTLED = new Set(["paid"]);
// ---- Ausgangsrechnungen ------------------------------------------------------
export function mapInvoice(raw, opts = {}) {
    const status = (raw.status ?? "").toLowerCase();
    if (INVOICE_STATUS_IGNORED.has(status))
        return null;
    const grossCents = toCents(raw.gross_total);
    if (grossCents === 0)
        return null;
    const netCents = toCents(raw.net_total);
    const vatCents = grossCents - netCents;
    const dueDate = raw.due_date ?? raw.date;
    const customerId = raw.customer?.id != null ? String(raw.customer.id)
        : raw.customer_id != null ? String(raw.customer_id)
            : null;
    const counterpartyName = raw.customer?.name ?? (customerId ? opts.companyNames?.get(customerId) : undefined) ?? "Unbekannt";
    // Bereits in MOCO erfasste Zahlungen uebernehmen — sonst wuerde eine dort
    // gebuchte Zahlung hier als weiterhin offen gelten.
    const payments = raw.payments ?? opts.paymentsByInvoice?.get(String(raw.id)) ?? null;
    const paidCents = sumPayments(payments);
    const matchedCents = Math.min(paidCents, grossCents);
    return {
        id: `invoice-${raw.id}`,
        source: "invoice",
        sourceId: String(raw.id),
        direction: "in",
        grossCents,
        netCents,
        vatCents,
        vatRatePercent: rateFrom(netCents, vatCents),
        reverseCharge: detectReverseCharge(raw, netCents, grossCents),
        expectedDate: dueDate,
        originalDate: dueDate,
        paymentDate: latestPaymentDate(payments),
        documentDate: raw.date,
        counterparty: counterpartyName,
        counterpartyId: customerId,
        counterpartyIban: null,
        reference: raw.identifier ?? null,
        category: "revenue",
        status: INVOICE_STATUS_SETTLED.has(status) || matchedCents >= grossCents
            ? "settled"
            : matchedCents > 0 ? "partiallyMatched" : "open",
        matchedCents,
        mocoPaidCents: matchedCents,
        currency: raw.currency ?? "EUR",
        title: raw.title ?? raw.identifier ?? `Rechnung ${raw.id}`,
        updatedAt: opts.now ?? new Date().toISOString(),
    };
}
// ---- Eingangsrechnungen ------------------------------------------------------
export function mapPurchase(raw, opts = {}) {
    const grossCents = toCents(raw.gross_total);
    if (grossCents === 0)
        return null;
    const netCents = toCents(raw.net_total);
    const vatCents = grossCents - netCents;
    const dueDate = raw.due_date ?? raw.date;
    const paidCents = sumPayments(raw.payments);
    const matchedCents = Math.min(paidCents, grossCents);
    return {
        id: `purchase-${raw.id}`,
        source: "purchase",
        sourceId: String(raw.id),
        direction: "out",
        grossCents,
        netCents,
        vatCents,
        vatRatePercent: rateFrom(netCents, vatCents),
        reverseCharge: detectPurchaseReverseCharge(raw, netCents, grossCents),
        expectedDate: dueDate,
        originalDate: dueDate,
        // Eingangsrechnungen liefern ihre Zahlungen direkt mit.
        paymentDate: latestPaymentDate(raw.payments),
        documentDate: raw.date,
        counterparty: raw.company?.name ?? "Unbekannt",
        counterpartyId: raw.company?.id != null ? String(raw.company.id) : null,
        counterpartyIban: normalizeIban(raw.iban),
        reference: raw.identifier ?? null,
        category: "materialCost",
        status: matchedCents >= grossCents ? "settled" : matchedCents > 0 ? "partiallyMatched" : "open",
        matchedCents,
        mocoPaidCents: matchedCents,
        currency: raw.currency ?? "EUR",
        title: raw.title ?? raw.identifier ?? `Eingangsrechnung ${raw.id}`,
        updatedAt: opts.now ?? new Date().toISOString(),
    };
}
// ---- Zahlungsplaene ----------------------------------------------------------
/**
 * Zahlungsplaene eines Festpreisprojekts als geplante Einnahme.
 *
 * Entscheidend ist der `billed`-Filter: sobald eine Rate abgerechnet wurde,
 * existiert dafuer eine echte Rechnung. Beide zu zaehlen waere exakt der
 * Doppelzaehlungsfehler, den dieses Tool verhindern soll.
 */
export function mapPaymentSchedule(raw, vatRatePercent, opts = {}) {
    if (raw.billed === true)
        return null;
    const netCents = toCents(raw.net_total);
    if (netCents === 0)
        return null;
    const vatCents = Math.round((netCents * vatRatePercent) / 100);
    const grossCents = netCents + vatCents;
    return {
        id: `schedule-${raw.id}`,
        source: "paymentSchedule",
        sourceId: String(raw.id),
        direction: "in",
        grossCents,
        netCents,
        vatCents,
        vatRatePercent,
        reverseCharge: false,
        expectedDate: raw.date,
        originalDate: raw.date,
        paymentDate: null,
        documentDate: raw.date,
        counterparty: raw.project?.customer?.name ?? raw.project?.name ?? "Unbekannt",
        counterpartyId: raw.project?.customer?.id != null ? String(raw.project.customer.id) : null,
        counterpartyIban: null,
        reference: raw.project?.identifier ?? null,
        category: "revenue",
        status: "open",
        matchedCents: 0,
        mocoPaidCents: 0,
        currency: "EUR",
        title: raw.title ?? `Zahlungsplan ${raw.project?.name ?? raw.id}`,
        updatedAt: opts.now ?? new Date().toISOString(),
    };
}
// ---- Fixkosten ---------------------------------------------------------------
/**
 * Expandiert eine MOCO-Fixkostenposition in monatliche Zahlungsposten.
 *
 * MOCO liefert hier nur Titel und Monatsbetraege — kein Steuerkennzeichen, kein
 * Netto/Brutto-Flag, keinen Zahltag. Diese Luecke schliesst das `mapping`, das
 * im Tool einmalig pro Position gepflegt wird.
 */
export function expandFixedCost(raw, mapping, range, opts = {}) {
    if (mapping && !mapping.enabled)
        return [];
    const now = opts.now ?? new Date().toISOString();
    const months = monthRange(range.from, range.to);
    const items = [];
    // Ohne gepflegtes Mapping gilt der Betrag als brutto und ohne Vorsteuer.
    //
    // Das ist die einzige Annahme, die den Cashflow nicht verfaelscht: der in
    // MOCO eingetragene Betrag ist der, der vom Konto geht. Waehlte man
    // stattdessen "netto plus 19 %", waeren alle Abfluesse um ein Fuenftel zu
    // hoch — bei Gehaeltern, Krankenkassen und Versicherungen, die gar keine
    // Umsatzsteuer tragen, waere das schlicht falsch. Die Vorsteuer ist dann
    // zwar zu niedrig angesetzt, das betrifft aber nur die Steuerprognose und
    // nicht die Liquiditaetskurve.
    const amountIsGross = mapping?.amountIsGross ?? true;
    const vatRatePercent = mapping?.vatRatePercent ?? 0;
    const category = mapping?.category ?? "other";
    const paymentDay = mapping?.paymentDayOfMonth ?? (Number(raw.payment_day) || 15);
    // MOCO wird erst zum Jahreswechsel fortgeschrieben. Ohne Fortschreibung
    // haette die Prognose ab Januar keine Fixkosten mehr und zeigte faelschlich
    // nach oben. Fehlt ein Monat, gilt derselbe Kalendermonat des zuletzt
    // gepflegten Jahres — so bleiben auch teilweise gefuellte Jahre lueckenlos.
    const known = new Map();
    for (const cost of raw.costs ?? []) {
        known.set(`${cost.year}-${String(cost.month).padStart(2, "0")}`, toCents(cost.amount));
    }
    /** Zuletzt bekannter Wert fuer diesen Kalendermonat, hoechstens aus dem Zieljahr. */
    const carryForward = (month) => {
        const jahr = Number(month.slice(0, 4));
        const mm = month.slice(5, 7);
        for (let y = jahr - 1; y >= jahr - 5; y--) {
            const wert = known.get(`${y}-${mm}`);
            if (wert !== undefined && wert !== 0)
                return wert;
        }
        return 0;
    };
    for (const month of months) {
        const amount = known.get(month) ?? carryForward(month);
        if (amount === 0)
            continue;
        const grossCents = amountIsGross ? amount : amount + Math.round((amount * vatRatePercent) / 100);
        const netCents = amountIsGross ? amount - Math.round((amount * vatRatePercent) / (100 + vatRatePercent)) : amount;
        const vatCents = grossCents - netCents;
        if (mapping?.splitPayroll) {
            items.push(...splitPayroll(raw, mapping, month, grossCents, opts.state, now));
            continue;
        }
        items.push({
            id: `fixed-${raw.id}-${month}`,
            source: "fixedCost",
            sourceId: `${raw.id}:${month}`,
            direction: "out",
            grossCents,
            netCents,
            vatCents,
            vatRatePercent,
            reverseCharge: mapping?.reverseCharge ?? false,
            expectedDate: dayInMonth(month, paymentDay),
            originalDate: dayInMonth(month, paymentDay),
            paymentDate: null,
            documentDate: dayInMonth(month, 1),
            counterparty: raw.title,
            counterpartyId: null,
            counterpartyIban: null,
            reference: null,
            category,
            status: "open",
            matchedCents: 0,
            mocoPaidCents: 0,
            currency: "EUR",
            title: `${raw.title} ${month}`,
            updatedAt: now,
        });
    }
    return items;
}
/**
 * Zerlegt eine Lohn-Fixkostenposition in ihre drei tatsaechlichen Abfluesse.
 *
 * In MOCO steht ein Monatsbetrag, auf dem Konto passieren drei Dinge zu
 * verschiedenen Terminen: Nettoloehne am letzten Bankarbeitstag, der
 * Sozialversicherungsbeitrag am drittletzten, die Lohnsteuer erst am 10. des
 * Folgemonats. Ohne diese Aufteilung zeigt die Kurve einen einzelnen grossen
 * Abfluss zum falschen Zeitpunkt.
 */
function splitPayroll(raw, mapping, month, grossCents, state, now) {
    const parts = [
        {
            key: "net", label: "Nettoloehne", percent: mapping.payrollNetSharePercent,
            date: nthLastBusinessDayOfMonth(month, 1, state),
        },
        {
            key: "social", label: "SV-Beitrag", percent: mapping.payrollSocialSharePercent,
            date: nthLastBusinessDayOfMonth(month, 3, state),
        },
        {
            key: "tax", label: "Lohnsteuer", percent: mapping.payrollWageTaxSharePercent,
            // Faellig am 10. des Folgemonats — dieselbe Frist wie die Umsatzsteuer.
            date: dayInMonth(nextMonth(month), 10),
        },
    ];
    const items = [];
    let assigned = 0;
    parts.forEach((part, index) => {
        // Der letzte Teil bekommt den Rest, damit die Summe exakt dem
        // Ausgangsbetrag entspricht und keine Cent-Differenz uebrigbleibt.
        const amount = index === parts.length - 1
            ? grossCents - assigned
            : Math.round((grossCents * part.percent) / 100);
        assigned += amount;
        if (amount === 0)
            return;
        items.push({
            id: `fixed-${raw.id}-${month}-${part.key}`,
            source: "payroll",
            sourceId: `${raw.id}:${month}:${part.key}`,
            direction: "out",
            grossCents: amount,
            netCents: amount,
            vatCents: 0,
            vatRatePercent: 0,
            reverseCharge: false,
            expectedDate: part.date,
            originalDate: part.date,
            paymentDate: null,
            documentDate: dayInMonth(month, 1),
            counterparty: part.key === "tax" ? "Finanzamt" : part.key === "social" ? "Krankenkassen" : raw.title,
            counterpartyId: null,
            counterpartyIban: null,
            reference: null,
            category: part.key === "tax" ? "tax" : "personnel",
            status: "open",
            matchedCents: 0,
            mocoPaidCents: 0,
            currency: "EUR",
            title: `${part.label} ${month}`,
            updatedAt: now,
        });
    });
    return items;
}
// ---- Banktransaktionen -------------------------------------------------------
/**
 * Uebersetzt eine Transaktion des Bank-SDK.
 *
 * Die Gegenpartei haengt an der Richtung: bei einem Abgang ist sie der
 * Empfaenger (creditor), bei einem Eingang der Zahler (debtor). Die Bank fuellt
 * jeweils nur eines der beiden Felder verlaesslich.
 */
export function mapBankTransaction(raw, accountId, opts = {}) {
    const amountCents = signedCents(raw.amount, raw.creditDebitIndicator);
    const isOutgoing = amountCents < 0;
    const bookingDate = raw.bookingDate ?? raw.valueDate ?? raw.transactionDate;
    if (!bookingDate) {
        throw new Error(`Banktransaktion ${raw.id} hat kein verwertbares Datum`);
    }
    const remittance = [raw.remittanceInformation, raw.note].filter(Boolean).join(" ").trim() || null;
    return {
        id: raw.id,
        accountId,
        amountCents,
        currency: raw.currency,
        bookingDate: bookingDate.slice(0, 10),
        valueDate: raw.valueDate ? raw.valueDate.slice(0, 10) : null,
        counterpartyName: (isOutgoing ? raw.creditorName : raw.debtorName) ?? raw.creditorName ?? raw.debtorName ?? null,
        counterpartyIban: normalizeIban(isOutgoing ? raw.creditorIban : raw.debtorIban) ?? normalizeIban(raw.creditorIban ?? raw.debtorIban),
        remittanceInformation: remittance,
        referenceNumber: raw.referenceNumber ?? null,
        bankTransactionCode: raw.bankTransactionCode ?? null,
        matchState: "unmatched",
        matchedCents: 0,
        balanceAfterCents: raw.balanceAfterTransaction != null ? toCents(raw.balanceAfterTransaction) : null,
        importedAt: opts.now ?? new Date().toISOString(),
    };
}
// ---- Hilfsfunktionen ---------------------------------------------------------
/** Datum der spaetesten erfassten Zahlung — massgeblich bei Ist-Versteuerung. */
function latestPaymentDate(payments) {
    if (!payments?.length)
        return null;
    const dates = payments.map((p) => p.date).filter((d) => Boolean(d)).sort();
    return dates.at(-1) ?? null;
}
function sumPayments(payments) {
    if (!payments)
        return 0;
    let sum = 0;
    for (const p of payments)
        sum += toCents(p.paid_total ?? p.total);
    return sum;
}
/**
 * Leitet den Steuersatz aus Netto und Steuerbetrag ab.
 *
 * Zuverlaessiger als das `tax`-Feld, das MOCO je nach Endpunkt mal als Satz und
 * mal als Betrag fuehrt. Gerundet, weil Positionssummen selten exakt aufgehen.
 */
function rateFrom(netCents, vatCents) {
    if (netCents <= 0 || vatCents === 0)
        return 0;
    return Math.round((vatCents / netCents) * 100);
}
/**
 * Reverse-Charge auf der Ausgangsseite.
 *
 * Ein explizites Kennzeichen aus MOCO hat Vorrang. Fehlt es, ist eine Rechnung
 * ohne jede Umsatzsteuer der Indikator — das trifft auch echte Steuerbefreiungen,
 * ist aber cash-wirksam identisch (in beiden Faellen fliesst keine Steuer).
 */
function detectReverseCharge(raw, netCents, grossCents) {
    if (typeof raw.reverse_charge === "boolean")
        return raw.reverse_charge;
    if (typeof raw.vat_code?.reverse_charge === "boolean")
        return raw.vat_code.reverse_charge;
    return netCents > 0 && netCents === grossCents;
}
function detectPurchaseReverseCharge(raw, netCents, grossCents) {
    if (typeof raw.reverse_charge === "boolean")
        return raw.reverse_charge;
    if (raw.items?.some((i) => i.vat_code?.reverse_charge === true))
        return true;
    return netCents > 0 && netCents === grossCents;
}
function nextMonth(month) {
    const year = Number(month.slice(0, 4));
    const m = Number(month.slice(5, 7));
    return m === 12 ? `${year + 1}-01` : `${year}-${String(m + 1).padStart(2, "0")}`;
}
/**
 * Markiert Posten, deren Faelligkeit vor dem Beginn unserer Bankhistorie liegt.
 *
 * Zu ihnen kann es keinen Abgleich mehr geben — die passende Buchung haben wir
 * nie gesehen. Sie weiter als offen zu fuehren waere nicht vorsichtig, sondern
 * falsch: ueberfaellige Posten werden in der Prognose nach vorn gerollt, und so
 * erschiene die gesamte Altlast als Abfluss der kommenden Woche.
 *
 * Bereits zugeordnete oder aus MOCO als bezahlt bekannte Posten bleiben
 * unberuehrt.
 */
export function markHistorical(items, observableFrom) {
    return items.map((item) => {
        if (item.status !== "open")
            return item;
        if (item.matchedCents > 0 || item.mocoPaidCents > 0)
            return item;
        if (item.expectedDate >= observableFrom)
            return item;
        return { ...item, status: "assumedSettled" };
    });
}
//# sourceMappingURL=ingest.js.map